/**
 * Learn Home — thin satellite-app wrapper around this agent's own board
 * (../components/learn), which is the SAME surface the main app renders in its
 * Home detail pane: it reaches the board through this app's face. Owns only the
 * standalone-app chrome: title, ReturnToMainAgent, MainAppNav, and the
 * "continue where you left off" card.
 *
 * The board itself (due-for-review, targets, recommendations, record) lives in
 * LearnBoardConnected — one implementation, so the satellite and the in-frame
 * face can never drift apart (MentorHome's precedent). Home is session-less;
 * the board's composer mints the session on its first send
 * (useDeferredFirstSend) and onSessionCreated navigates into it — there is no
 * empty-session "open assistant" step, because tricorder-session creates a
 * session WITH its first message, never without one.
 *
 * GET /sessions lists every session the caller may read (no agent filter on
 * the route), so the resume card filters to this agent client-side. Run
 * sessions died with the training run, so every learn-agent session left is a
 * conversation.
 */
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ReturnToMainAgent } from '@frontend/components/ReturnToMainAgent'
import { MainAppNav } from '@frontend/components/MainAppNav'
import { useSessions } from '@frontend/hooks/useSessions'
import { LearnBoardConnected } from '../components/learn'
import { AGENT_SLUG } from '../agent'

export default function LearnHome() {
  const { t } = useTranslation()
  const navigate = useNavigate()

  // The newest learn conversation the caller may read.
  const recentSessions = useSessions()
  const lastConversation = (recentSessions.data ?? []).find((s) => s.agent?.slug === AGENT_SLUG)

  return (
    <div className="flex h-dvh flex-col bg-[var(--color-surface-canvas,#ffffff)]">
      <header className="flex items-center gap-2 border-b border-neutral-200 bg-white px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <ReturnToMainAgent className="-ml-2" />
        <h1 className="min-w-0 flex-1 truncate text-[19px] font-semibold tracking-[-0.01em] text-neutral-900">
          {t('learn.homeTitle', 'Learn')}
        </h1>
        <MainAppNav />
      </header>

      {/* Column, not a scroller: the board owns its own scroll region (it has
          to — the Session page docks a chat under it), so the resume card sits
          above it as a fixed strip rather than inside a second scroller. */}
      <main className="flex min-h-0 flex-1 flex-col">
        {lastConversation && (
          <div className="mx-auto w-full max-w-3xl shrink-0 px-4 pt-4">
            <button
              type="button"
              onClick={() => navigate(`/sessions/${lastConversation.id}`)}
              className="w-full rounded-[14px] border border-neutral-200 bg-white p-4 text-left transition hover:bg-neutral-50"
            >
              <p className="text-[13px] font-semibold uppercase tracking-wide text-neutral-500">
                {t('learn.resume.label', 'Continue where you left off')}
              </p>
              <p className="mt-1 truncate text-[15px] font-semibold text-neutral-900">
                {lastConversation.title || t('learn.resume.untitled', 'Your conversation')}
              </p>
            </button>
          </div>
        )}

        <div className="min-h-0 flex-1">
          <LearnBoardConnected onSessionCreated={(s) => navigate(`/sessions/${s.id}`)} />
        </div>
      </main>
    </div>
  )
}
